import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CellRenderer } from '@shared/interfaces/table/cell-renderer';

/**
 * Component responsible for rendering clickable links in table cells.
 * Implements the CellRenderer interface for table integration.
 *
 * This component is primarily used through table column configurations to create
 * navigable links within table cells. It supports both static and dynamic URLs
 * based on row data.
 *
 * ```typescript
 * {
 *   title: 'Details',
 *   key: 'name',
 *   cellRenderer: LinkCellComponent,
 *   cellRendererParams: {
 *     url: '/details',     // Base URL
 *     urlReference: 'id'   // Optional: Field to append to URL
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-link-cell',
  imports: [
    RouterLink,
  ],
  templateUrl: './link-cell.component.html',
  styleUrl: './link-cell.component.css'
})
export class LinkCellComponent extends CellRenderer implements OnInit {

  /**
   * Constructed URL for the router link.
   * Generated during initialization by combining the base URL with dynamic data.
   */
  public url?: string;

  /**
   * Initializes the component by setting up the display value and constructing the URL.
   */
  public ngOnInit(): void {
    this.value = this.data[this.params['key']];
    this.url = this.constructUrl();
  }

  /**
   * Constructs the complete URL by combining the base URL with optional dynamic data.
   *
   * @returns {string | undefined} The constructed URL or undefined if no base URL is provided
   *
   */
  public constructUrl(): string | undefined {
    if (this.params?.cellRendererParams?.url) {
      let url: string = this.params.cellRendererParams.url;
      if (this.params.cellRendererParams?.urlReference) {
        const urlRef: string = this.params.cellRendererParams.urlReference;
        if (this.data[urlRef]) {
          url = `${url}/${encodeURI(this.data[urlRef])}`;
        }
      }
      return `${url}`;
    }
    return;
  }
}
